import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Trash2, ArrowLeft, Pencil, ExternalLink, Handshake, Zap, ImageOff } from "lucide-react";
import FavoriteButton from "../components/FavoriteButton";
import Lightbox from "../components/Lightbox";
import SellModal from "../components/SellModal";
import BoostModal from "../components/BoostModal";

export default function ListingDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [sellOpen, setSellOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(r => setListing(r.data)).catch(() => setListing(null));
  }, [id]);

  const sendMessage = async () => {
    if (!user) { nav("/login"); return; }
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { recipient_id: listing.user_id, listing_id: listing.id, content: msg });
      toast.success("Message sent");
      setMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/listings/${id}`);
    toast.success(t("listing_deleted"));
    nav("/marketplace");
  };

  if (!listing) return <div className="pt-32 text-center text-sm text-[#1A1F1C]/60">Loading...</div>;

  const isOwner = user?.id === listing.user_id;
  const imgs = listing.image_ids || [];
  const uploadedUrls = imgs.map(iid => `${API}/files/${iid}`);
  const exampleUrl = listing.is_example ? listing.external_image_url : null;
  const imgUrls = uploadedUrls.length ? uploadedUrls : (exampleUrl ? [exampleUrl] : []);
  const hasImage = imgUrls.length > 0;

  return (
    <div data-testid="listing-detail-page" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-[#143122] hover:underline mb-6">
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("browse")}
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-3">
            <div
              className="aspect-[4/3] w-full bg-[#F4F1E9] rounded-lg overflow-hidden border border-[#E6E2D8] relative"
              onClick={() => hasImage && setLightbox(activeImg)}
              data-testid="main-image-container"
              style={{ cursor: hasImage ? "zoom-in" : "default" }}
            >
              {hasImage ? (
                <img
                  data-testid="listing-main-image"
                  src={imgUrls[activeImg]}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  data-testid="listing-no-image"
                  className="w-full h-full flex flex-col items-center justify-center text-[#1A1F1C]/40 gap-3"
                >
                  <ImageOff size={40} strokeWidth={1.25} />
                  <span className="text-xs uppercase tracking-wider">{t("no_photo")}</span>
                </div>
              )}
              <div className="absolute top-3 right-3">
                <FavoriteButton listingId={listing.id} size={18} />
              </div>
              {listing.sold && (
                <div className="absolute top-3 left-3 kav-chip !bg-[#143122] !text-white !border-[#143122]">
                  {t("sold_label")}
                </div>
              )}
              {listing.is_example && !listing.sold && (
                <div className="absolute top-3 left-3 kav-chip !bg-[#143122] !text-white !border-[#143122]">
                  {t("example_badge")}
                </div>
              )}
              {!listing.sold && listing.boosted_until && new Date(listing.boosted_until) > new Date() && (
                <div
                  data-testid="boost-badge"
                  className={`absolute inline-flex items-center gap-1 kav-chip !bg-[#B58A2F] !text-white !border-[#B58A2F] ${listing.is_example ? "top-11 left-3" : "top-3 left-3"}`}
                >
                  <Zap size={12} strokeWidth={1.75} fill="#FAFAFA" /> {t("boosted")}
                </div>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {imgs.map((imgId, i) => (
                  <button
                    key={imgId}
                    data-testid={`thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 rounded-md overflow-hidden border transition-all ${activeImg === i ? "border-[#143122]" : "border-[#E6E2D8] opacity-70"}`}
                  >
                    <img src={`${API}/files/${imgId}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="overline text-[#143122]">{listing.type}</div>
            <h1 data-testid="listing-title" className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122] leading-tight">
              {listing.title}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-sm text-[#1A1F1C]/70">
              <MapPin size={14} strokeWidth={1.5} /> {listing.location}
            </div>
            {listing.price != null && (
              <div className="mt-6 font-serif-display text-3xl text-[#143122]">
                {listing.price.toLocaleString()} {listing.currency}
              </div>
            )}

            <p className="mt-6 text-sm leading-relaxed text-[#1A1F1C]/80 whitespace-pre-line">
              {listing.description}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {listing.breed && <span className="kav-chip">{t("breed")}: {listing.breed}</span>}
              {listing.age != null && <span className="kav-chip">{t("age")}: {listing.age}</span>}
              {listing.gender && <span className="kav-chip">{listing.gender}</span>}
              {listing.discipline && <span className="kav-chip">{listing.discipline}</span>}
              {listing.tack_type && <span className="kav-chip">{t(`tack_${listing.tack_type}`)}</span>}
              {listing.condition && <span className="kav-chip">{t(`condition_${listing.condition}`)}</span>}
              {listing.size && <span className="kav-chip">{t("size")}: {listing.size}</span>}
              {listing.boarding_type && <span className="kav-chip">{t(`boarding_${listing.boarding_type}`)}</span>}
              {listing.capacity != null && <span className="kav-chip">{listing.capacity} {t("horses_capacity")}</span>}
              {listing.amenities && <span className="kav-chip">{listing.amenities}</span>}
              {listing.rental_type && <span className="kav-chip">{t(`rental_${listing.rental_type}`)}</span>}
              {listing.category && <span className="kav-chip">{listing.category}</span>}
            </div>

            <div className="mt-8 border-t border-[#E6E2D8] pt-6">
              <div className="overline text-[#143122]">{t("posted_by")}</div>
              <Link
                to={`/users/${listing.user_id}`}
                data-testid="posted-by-link"
                className="mt-1 font-serif-display text-2xl text-[#1A1F1C] hover:text-[#143122] transition-colors inline-flex items-center gap-2"
              >
                {listing.user_name}
                <ExternalLink size={14} strokeWidth={1.5} />
              </Link>
              <div className="mt-3 space-y-1.5 text-sm text-[#1A1F1C]/75">
                {listing.contact_email && (
                  <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.5} /> {listing.contact_email}</div>
                )}
                {listing.contact_phone && (
                  <div className="flex items-center gap-2"><Phone size={14} strokeWidth={1.5} /> {listing.contact_phone}</div>
                )}
              </div>
            </div>

            {!isOwner ? (
              listing.is_example ? (
                <div data-testid="example-notice" className="mt-6 border border-[#E6E2D8] rounded-lg p-4 bg-[#F4F1E9]">
                  <div className="overline text-[#143122]">{t("example_badge")}</div>
                  <div className="mt-1 text-sm text-[#1A1F1C]/80">
                    {t("example_note")}
                  </div>
                </div>
              ) : (
                <div className="mt-6">
                <textarea
                  data-testid="message-input"
                  rows={3}
                  className="kav-input"
                  placeholder={t("write_message")}
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                />
                <button
                  data-testid="send-message-btn"
                  disabled={busy || !msg.trim()}
                  onClick={sendMessage}
                  className="kav-btn-primary mt-3 w-full justify-center"
                >
                  {t("send_message")}
                </button>
              </div>
              )
            ) : (
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  data-testid="edit-listing-btn"
                  onClick={() => nav(`/listings/${id}/edit`)}
                  className="kav-btn-primary text-sm inline-flex items-center gap-2"
                >
                  <Pencil size={14} strokeWidth={1.5} /> {t("edit_listing")}
                </button>
                {!listing.is_example && (
                  <button
                    data-testid="boost-listing-btn"
                    onClick={() => setBoostOpen(true)}
                    className="kav-btn-secondary text-sm inline-flex items-center gap-2"
                  >
                    <Zap size={14} strokeWidth={1.75} /> {t("boost")}
                  </button>
                )}
                {!listing.sold && (
                  <button
                    data-testid="mark-sold-btn"
                    onClick={() => setSellOpen(true)}
                    className="kav-btn-secondary text-sm inline-flex items-center gap-2"
                  >
                    <Handshake size={14} strokeWidth={1.5} /> {t("mark_as_sold")}
                  </button>
                )}
                <button
                  data-testid="delete-listing-btn"
                  onClick={remove}
                  className="kav-btn-secondary text-sm inline-flex items-center gap-2"
                >
                  <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {lightbox != null && (
        <Lightbox
          images={imgUrls}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onIndex={(i) => { setLightbox(i); setActiveImg(i); }}
        />
      )}

      <SellModal
        open={sellOpen}
        onClose={() => setSellOpen(false)}
        listing={listing}
        onDone={() => api.get(`/listings/${id}`).then(r => setListing(r.data))}
      />
      <BoostModal
        open={boostOpen}
        onClose={() => setBoostOpen(false)}
        listing={listing}
      />
    </div>
  );
}
